import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  TrackerScreens,
  TrackerWithRecipientNames,
} from "../../interfaces/interfaces";
import {
  fetchAsyncTrackerByDate,
  fetchAsyncTrackerSummary,
  fetchAyncTrackersByType,
  refreshAsyncTrackerSummary,
} from "./trackerUtil";

export interface TrackerState {
  activeTrackersCount: number;
  overdueTrackersCount: number;
  completedTrackersCount: number;
  lastMonthTrackers: TrackerWithRecipientNames[];
  topOverdueTrackers: TrackerWithRecipientNames[];
  topUpcomingTrackers: TrackerWithRecipientNames[];
  activeTrackers: TrackerWithRecipientNames[];
  overdueTrackers: TrackerWithRecipientNames[];
  completedTrackers: TrackerWithRecipientNames[];
  error: any;
  loading: boolean;
  activeTrackerId: number;
  currentScreen: string;
  trackerScreenLoading: boolean;
}

const INITIAL_STATE = {
  activeTrackersCount: 0,
  overdueTrackersCount: 0,
  completedTrackersCount: 0,
  lastMonthTrackers: [],
  topOverdueTrackers: [],
  topUpcomingTrackers: [],
  error: null,
  loading: false,
  trackerScreenLoading: false,
  currentScreen: TrackerScreens.summary,
  activeTrackers: null,
  overdueTrackers: null,
  completedTrackers: null,
} as TrackerState;

type UpdateTrackerPayload = {
  trackerIndex: number;
  tracker: TrackerWithRecipientNames;
  trackerType: string;
};

type SetTrackerClosePayload = {
  trackerIndex: number;
  tracker: TrackerWithRecipientNames;
  trackerType: string;
};

type RemoveTrackerCloseStatusPayload = {
  trackerIndex: number;
  tracker: TrackerWithRecipientNames;
  trackerType: string;
  addToSection: string;
};

type DeleteTrackerPayload = {
  trackerIndex: number;
  trackerId: number;
  trackerType: string;
};

type UpdateTrackerNotificationPayload = {
  tracker: TrackerWithRecipientNames;
  oldStatus: string;
  newStatus: string;
};

const TrackerSlice = createSlice({
  name: "tracker",
  initialState: INITIAL_STATE,
  reducers: {
    setTrackerClose: (state, action: PayloadAction<SetTrackerClosePayload>) => {
      const tracker = action.payload.tracker;
      const trackerIndex = action.payload.trackerIndex;
      // let oldTrackers = [];
      switch (action.payload.trackerType) {
        case "active":
          const oldActiveTrackers = [...state.activeTrackers];
          oldActiveTrackers.splice(trackerIndex, 1);
          state.activeTrackers = oldActiveTrackers.splice(trackerIndex, 1);
          // state.activeTrackers[action.payload.trackerIndex] = tracker;
          break;
        case "overdue":
          const oldOverdueTrackers = [...state.overdueTrackers];
          oldOverdueTrackers.splice(trackerIndex, 1);
          state.overdueTrackers = oldOverdueTrackers;
          break;
        default:
          break;
      }
      state.completedTrackers = state.completedTrackers
        ? [tracker, ...state.completedTrackers]
        : null;
    },
    removeTrackerOnClose: (
      state,
      action: PayloadAction<RemoveTrackerCloseStatusPayload>
    ) => {
      const { addToSection, tracker, trackerIndex, trackerType } =
        action.payload;
      const oldCompletedTracker = [...state.completedTrackers];
      oldCompletedTracker.splice(trackerIndex, 1);
      state.completedTrackers = oldCompletedTracker;
      switch (addToSection) {
        case "active":
          state.activeTrackers = state.activeTrackers
            ? [tracker, ...state.activeTrackers]
            : null;
          break;
        case "overdue":
          state.overdueTrackers = state.overdueTrackers
            ? [tracker, ...state.overdueTrackers]
            : null;
          break;
        default:
          break;
      }
    },
    deleteTracker: (state, action: PayloadAction<DeleteTrackerPayload>) => {
      const { trackerId, trackerIndex, trackerType } = action.payload;
      switch (trackerType) {
        case "active":
          const oldActiveTrackers = [...state.activeTrackers];
          oldActiveTrackers.splice(trackerIndex, 1);
          state.activeTrackers = oldActiveTrackers;
          break;
        case "overdue":
          const oldOverdueTrackers = [...state.overdueTrackers];
          oldOverdueTrackers.splice(trackerIndex, 1);
          state.overdueTrackers = oldOverdueTrackers;
          break;
        case "completed":
          const oldCompletedTrackers = [...state.completedTrackers];
          oldCompletedTrackers.splice(trackerIndex, 1);
          state.completedTrackers = oldCompletedTrackers;
          break;
        default:
          break;
      }
    },
    updateTracker: (state, action: PayloadAction<UpdateTrackerPayload>) => {
      const { trackerIndex, trackerType, tracker } = action.payload;
      let addToSection = tracker.dateDiff >= 0 ? "overdue" : "active";
      if (addToSection === action.payload.trackerType) {
        switch (action.payload.trackerType) {
          case "active":
            state.activeTrackers[action.payload.trackerIndex] = tracker;
            break;
          case "overdue":
            state.overdueTrackers[action.payload.trackerIndex] = tracker;
            break;
          case "completed":
            state.completedTrackers[action.payload.trackerIndex] = tracker;
            break;
          default:
            break;
        }
      } else {
        switch (trackerType) {
          case "active":
            const oldActiveTrackers = [...state.activeTrackers];
            oldActiveTrackers.splice(trackerIndex, 1);
            state.activeTrackers = oldActiveTrackers;
            break;
          case "overdue":
            const oldOverdueTrackers = [...state.overdueTrackers];
            oldOverdueTrackers.splice(trackerIndex, 1);
            state.overdueTrackers = oldOverdueTrackers;
            break;
          case "completed":
            const oldCompletedTrackers = [...state.completedTrackers];
            oldCompletedTrackers.splice(trackerIndex, 1);
            state.completedTrackers = oldCompletedTrackers;
            break;
          default:
            break;
        }
      }
      // state.trackers[action.payload.trackerIndex] = action.payload.tracker;
    },
    setActiveScreen: (state, action: PayloadAction<string>) => {
      state.currentScreen = action.payload;
    },
    deleteTrackerFromNotification: (
      state,
      action: PayloadAction<{ trackerId: number[] }>
    ) => {
      let toRemoveTrackerIds = action.payload.trackerId;
      if (state.activeTrackers !== null) {
        const oldActiveTrackers = [...state.activeTrackers];
        const newActiveTrackers = oldActiveTrackers.filter((item) => {
          if (toRemoveTrackerIds.includes(item.id)) {
            //
            return false;
          } else {
            return true;
          }
        });
        state.activeTrackers = newActiveTrackers;
      }
      if (state.overdueTrackers !== null) {
        const oldOverdueTrackers = [...state.overdueTrackers];
        const newOverdueTrackers = oldOverdueTrackers.filter((item) => {
          if (toRemoveTrackerIds.includes(item.id)) {
            return false;
          } else {
            return true;
          }
        });
        state.overdueTrackers = newOverdueTrackers;
      }

      if (state.completedTrackers !== null) {
        const oldCompletedTrackers = [...state.completedTrackers];
        const newCompleteTrackers = oldCompletedTrackers.filter((item) => {
          if (toRemoveTrackerIds.includes(item.id)) {
            return false;
          } else {
            return true;
          }
        });
        state.completedTrackers = newCompleteTrackers;
      }
    },
    addTrackerFromNotification: (
      state,
      action: PayloadAction<{ tracker: TrackerWithRecipientNames }>
    ) => {
      const tracker = action.payload.tracker;
      if (tracker !== undefined) {
        let addToSection = tracker.dateDiff >= 0 ? "overdue" : "active";
        switch (addToSection) {
          case "active":
            state.activeTrackers = state.activeTrackers
              ? [tracker, ...state.activeTrackers]
              : null;
            break;
          case "overdue":
            state.overdueTrackers = state.overdueTrackers
              ? [tracker, ...state.overdueTrackers]
              : null;
            break;
          default:
            break;
        }
      }
    },
    updateTrackerFromNotification: (
      state,
      action: PayloadAction<UpdateTrackerNotificationPayload>
    ) => {
      const { tracker, oldStatus, newStatus } = action.payload;
      switch (oldStatus) {
        case "active":
          state.activeTrackers = state.activeTrackers
            ? state.activeTrackers.filter((item) => item.id !== tracker.id)
            : null;
          break;
        case "overdue":
          state.overdueTrackers = state.overdueTrackers
            ? state.overdueTrackers.filter((item) => item.id !== tracker.id)
            : null;
          break;
        case "completed":
          state.completedTrackers = state.completedTrackers
            ? state.completedTrackers.filter((item) => item.id !== tracker.id)
            : null;
          break;
        default:
          break;
      }

      switch (newStatus) {
        case "active":
          state.activeTrackers = state.activeTrackers
            ? [tracker, ...state.activeTrackers]
            : null;
          break;
        case "overdue":
          state.overdueTrackers = state.overdueTrackers
            ? [tracker, ...state.overdueTrackers]
            : null;
          break;
        case "completed":
          state.completedTrackers = state.completedTrackers
            ? [tracker, ...state.completedTrackers]
            : null;
          break;
        default:
          break;
      }
    },
    resetTracker: () => INITIAL_STATE,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAsyncTrackerSummary.pending, (state, action) => {
      state.error = null;
      state.loading = true;
    });
    builder.addCase(fetchAsyncTrackerSummary.fulfilled, (state, action) => {
      state.loading = false;
      const {
        activeTrackersCount,
        overdueTrackersCount,
        closedTrackersCount,
        lastMonthTrackers,
        topOverdueTrackers,
        topUpcomingTrackers,
      } = action.payload;

      state.activeTrackersCount = activeTrackersCount;
      state.overdueTrackersCount = overdueTrackersCount;
      state.completedTrackersCount = closedTrackersCount;
      state.lastMonthTrackers = lastMonthTrackers;
      state.topOverdueTrackers = topOverdueTrackers;
      state.topUpcomingTrackers = topUpcomingTrackers;
      state.loading = false;
    });
    builder.addCase(fetchAsyncTrackerSummary.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });
    builder.addCase(fetchAyncTrackersByType.pending, (state, action) => {
      state.error = null;
      state.trackerScreenLoading = true;
    });
    builder.addCase(fetchAyncTrackersByType.fulfilled, (state, action) => {
      const trackers = action.payload.trackers;
      switch (action.payload.type) {
        case "active":
          state.activeTrackers = trackers;
          break;
        case "overdue":
          state.overdueTrackers = trackers;
          break;
        case "completed":
          state.completedTrackers = trackers;
          break;
        default:
          break;
      }
      state.trackerScreenLoading = false;
    });
    builder.addCase(fetchAyncTrackersByType.rejected, (state, action) => {
      state.error = action.error.message;
      state.trackerScreenLoading = false;
    });
    builder.addCase(fetchAsyncTrackerByDate.pending, (state, action) => {
      state.error = null;
      state.trackerScreenLoading = true;
    });
    builder.addCase(fetchAsyncTrackerByDate.fulfilled, (state, action) => {
      const trackers = action.payload.filteredTracker;
      switch (action.payload.trackerType) {
        case "active":
          state.activeTrackers = trackers;
          break;
        case "overdue":
          state.overdueTrackers = trackers;
          break;
        case "completed":
          state.completedTrackers = trackers;
          break;
        default:
          break;
      }
      state.trackerScreenLoading = false;
    });
    builder.addCase(fetchAsyncTrackerByDate.rejected, (state, action) => {
      state.error = action.error.message;
      state.trackerScreenLoading = false;
    });
    builder.addCase(refreshAsyncTrackerSummary.fulfilled, (state, action) => {
      state.loading = false;
      const {
        activeTrackersCount,
        overdueTrackersCount,
        closedTrackersCount,
        lastMonthTrackers,
        topOverdueTrackers,
        topUpcomingTrackers,
      } = action.payload;

      state.activeTrackersCount = activeTrackersCount;
      state.overdueTrackersCount = overdueTrackersCount;
      state.completedTrackersCount = closedTrackersCount;
      state.lastMonthTrackers = lastMonthTrackers;
      state.topOverdueTrackers = topOverdueTrackers;
      state.topUpcomingTrackers = topUpcomingTrackers;
      state.loading = false;
    });
  },
});

export const {
  updateTracker,
  setTrackerClose,
  removeTrackerOnClose,
  deleteTracker,
  setActiveScreen,
  resetTracker,
  deleteTrackerFromNotification,
  addTrackerFromNotification,
  updateTrackerFromNotification,
} = TrackerSlice.actions;

export const TrackerSliceReducer = TrackerSlice.reducer;
